import React, { useContext, useEffect, useMemo, useState } from "react";
import { useIonAlert, useIonToast } from "@ionic/react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { FB_COLLECTION_PATH } from "../../functions/postActionTypes";
import { ProfileContext } from "../../context/ProfileContext";
import {
  markAnnouncementAsRead,
  softDeleteAnnouncement,
} from "../../domain/announcements/announcementActions";

interface AnnouncementDetailPanelProps {
  announcementId: string | null;
  onEditRequest: (announcementId: string) => void;
  onDeleted?: () => void;
}

export const AnnouncementDetailPanel: React.FC<AnnouncementDetailPanelProps> = ({
  announcementId,
  onEditRequest,
  onDeleted,
}) => {
  const { profile } = useContext(ProfileContext);
  const [presentAlert] = useIonAlert();
  const [presentToast] = useIonToast();

  const [announcement, setAnnouncement] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);

  const isSchedulerMode = profile?.UserLastRoleScheduler === true;
  const isAuthor = useMemo(() => {
    return String(announcement?.authorUid || "") === String(profile?.UserId || "");
  }, [announcement?.authorUid, profile?.UserId]);
  const canManageAnnouncement = isSchedulerMode && isAuthor;

  useEffect(() => {
    if (!announcementId) {
      setAnnouncement(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const ref = doc(db, FB_COLLECTION_PATH.Announcements, announcementId);

    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setAnnouncement(null);
          setLoading(false);
          return;
        }

        const data: any = snap.data() || {};
        if (String(data?.status || "active") === "deleted") {
          setAnnouncement(null);
          setLoading(false);
          return;
        }

        setAnnouncement({
          announcementId: snap.id,
          ...data,
        });
        setLoading(false);
      },
      () => {
        setAnnouncement(null);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [announcementId]);

  useEffect(() => {
    const uid = String(profile?.UserId || "");
    const id = String(announcementId || "");
    if (!uid || !id) return;

    markAnnouncementAsRead({ userId: uid, announcementId: id }).catch((_error) => {
      // console.log("desktop markAnnouncementAsRead error:", error);
    });
  }, [profile?.UserId, announcementId]);

  const handleDelete = async () => {
    if (!announcement?.announcementId || !canManageAnnouncement) return;

    presentAlert({
      header: "Delete Announcement",
      message: "Are you sure you want to delete this announcement?",
      buttons: [
        { text: "Cancel", role: "cancel" },
        {
          text: "Delete",
          role: "destructive",
          handler: async () => {
            try {
              await softDeleteAnnouncement(announcement.announcementId);
              await presentToast({ message: "Announcement deleted", duration: 1300, position: "top", color: "medium" });
              onDeleted?.();
            } catch (error) {
              // console.log("desktop delete announcement error:", error);
            }
          },
        },
      ],
    });
  };

  if (!announcementId) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", opacity: 0.8 }}>
        <div style={{ textAlign: "center" }}>
          <h3 style={{ marginBottom: 8 }}>Select an announcement</h3>
          <p style={{ margin: 0, color: "var(--ion-color-medium)" }}>Announcement details will appear here.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!announcement) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", opacity: 0.8 }}>
        <div style={{ textAlign: "center" }}>
          <h3 style={{ marginBottom: 8 }}>Announcement unavailable</h3>
          <p style={{ margin: 0, color: "var(--ion-color-medium)" }}>This announcement may have been deleted.</p>
        </div>
      </div>
    );
  }

  const dateText = Number(announcement?.createdAt)
    ? new Date(Number(announcement.createdAt)).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

  const actionBtnBase: React.CSSProperties = {
    borderRadius: 999,
    padding: "0",
    paddingInline: "12px",
    fontSize: "0.82rem",
    fontWeight: 600,
    height: "36px",
    minHeight: "36px",
    maxHeight: "36px",
    minWidth: "72px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: "1",
    textAlign: "center",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    margin: 0,
    fontFamily: "inherit",
    boxSizing: "border-box",
    appearance: "none",
    WebkitAppearance: "none",
    cursor: "pointer",
    transition: "all 0.15s ease",
    borderStyle: "solid",
    borderWidth: "1px",
    borderColor: "var(--ion-color-step-250, rgba(255,255,255,0.18))",
    background: "var(--ion-color-step-50, rgba(255,255,255,0.04))",
    color: "var(--ion-text-color)",
  };

  return (
    <div style={{ height: "100%", overflowY: "auto", padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Announcement</h2>
        {canManageAnnouncement && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => onEditRequest(announcement.announcementId)}
              style={actionBtnBase}
            >
              Edit
            </button>
            <button
              type="button"
              onClick={handleDelete}
              style={{
                ...actionBtnBase,
                border: "1px solid var(--ion-color-danger, #b00020)",
                color: "var(--ion-color-danger, #b00020)",
                background: "transparent",
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <img
          src={announcement?.authorProfilePicURL || "../assets/profilePic.png"}
          alt=""
          style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }}
        />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700 }}>{announcement?.authorName || "Unknown Author"}</div>
          <div style={{ fontSize: "0.85rem", color: "var(--ion-color-medium)" }}>
            {announcement?.authorCompany || ""}
          </div>
        </div>
        <div style={{ marginLeft: "auto", textAlign: "right" }}>
          <div style={{ fontSize: "0.8rem", color: "var(--ion-color-medium)" }}>{dateText}</div>
        </div>
      </div>

      <h3 style={{ marginTop: 0, marginBottom: 12 }}>{announcement?.title || ""}</h3>

      <p style={{ whiteSpace: "pre-wrap", marginTop: 0, lineHeight: 1.5 }}>{announcement?.body || ""}</p>

      {!!announcement?.imageUrl && (
        <img
          src={announcement.imageUrl}
          alt="Announcement"
          style={{
            width: "100%",
            maxHeight: 480,
            objectFit: "contain",
            background: "rgba(0,0,0,0.04)",
            borderRadius: 12,
            marginTop: 10,
          }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      )}
    </div>
  );
};
